import React from 'react';
import { isNull } from 'lodash';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LzSAppBasics from '@lz-app/AppBasics';
import LzPAppBootstrap from '@lz-app/Bootstrap/type';
import { LzTNavItem, LzTNavItems } from '@lz-layouts/Navigation/types';
import { LzTOpenObject } from '@lz-types/types';
import { LzTRouteEntries, LzTRouteEntry } from '@lz-services/Routes';
import LzPTemplate from '@lz-templates/type';
import LzXModernWithLeftNavTemplate from '@lz-templates/ModernWithLeftNav';
import LzXPageNavParent from '@lz-screens/commons/NavParent';
import LzXPageLogin from '@lz-screens/public/user/Login';
import LzXPageForgotPassword from '@lz-screens/public/user/ForgotPassword';
import LzXPageResetPassword from '@lz-screens/public/user/ResetPassword';
import LzXPageRegister from '@lz-screens/public/user/Register';
import LzAImgLogo from '@lz-assets/images/logo.png';
import LzAImgBgBase from '@lz-assets/images/bg1.jpg';
import LzAImgBgLogin from '@lz-assets/images/bg-login.jpg';
import LzAImgLzLogo from '@lz-assets/images/lz-logo.png';
import LzCModule from '@lz-modules/Module';
import { LzTModuleChildClass, LzTModuleChildCollection } from '@lz-modules/types';
import { lzFIsArray } from '@lz-services/Type';
import LzCEntity from '@lz-modules/Entity';
import LzCReport from '@lz-modules/Report';
import LzCPage from '@lz-modules/Page';

type LzTAppInit = {
    modules?: LzTModuleChildCollection;
    WrapperTemplate?: (props: LzPTemplate) => React.ReactNode;
    logo_image?: string;
    base_bg_image?: string;
    login_bg_image?: string;
    register_bg_image?: string;
    forgot_pwd_bg_image?: string;
    powered_by_image?: string;
    powered_by_url?: string;
    hide_powered_by?: boolean;
    date_format?: string;
    time_format?: string;
    datetime_format?: string;
    all_api_calls_extra_params?: LzTOpenObject;
};

export default class LzSApp {
    private static _modules: LzTModuleChildCollection;
    private static _WrapperTemplate: (props: LzPTemplate) => React.ReactNode;
    private static _logo_image: string;
    private static _base_bg_image: string;
    private static _login_bg_image: string;
    private static _register_bg_image: string;
    private static _forgot_pwd_bg_image: string;
    private static _powered_by_image: string;
    private static _powered_by_url: string;
    private static _hide_powered_by: boolean;
    private static _date_format: string;
    private static _time_format: string;
    private static _datetime_format: string;
    private static _all_api_calls_extra_params: LzTOpenObject;

    public static init(params: LzTAppInit) {
        LzSApp._modules = params.modules || [];
        LzSApp._WrapperTemplate = typeof params.WrapperTemplate !== 'undefined' ? params.WrapperTemplate : LzXModernWithLeftNavTemplate;
        LzSApp._logo_image = params.logo_image || LzAImgLogo;
        LzSApp._base_bg_image = params.base_bg_image || LzAImgBgBase;
        LzSApp._login_bg_image = params.login_bg_image || LzAImgBgLogin;
        LzSApp._register_bg_image = params.register_bg_image || LzAImgBgLogin;
        LzSApp._forgot_pwd_bg_image = params.forgot_pwd_bg_image || LzAImgBgLogin;
        LzSApp._powered_by_image = params.powered_by_image || LzAImgLzLogo;
        LzSApp._powered_by_url = params.powered_by_url || 'https://logic-zone.net/#ref:' + LzSAppBasics.app_code;
        LzSApp._hide_powered_by = !!params.hide_powered_by;
        LzSApp._date_format = params.date_format || 'DD/MM/YYYY';
        LzSApp._time_format = params.time_format || 'hh:mma';
        LzSApp._datetime_format = params.datetime_format || 'DD/MM/YYYY hh:mma';
        LzSApp._all_api_calls_extra_params = {};
    }

    public static get modules(): LzTModuleChildCollection {
        return LzSApp._modules;
    }
    public static get WrapperTemplate(): (props: LzPTemplate) => React.ReactNode {
        return LzSApp._WrapperTemplate || LzXModernWithLeftNavTemplate;
    }
    public static get logo_image(): string {
        return LzSApp._logo_image;
    }
    public static get base_bg_image(): string {
        return LzSApp._base_bg_image;
    }
    public static get login_bg_image(): string {
        return LzSApp._login_bg_image;
    }
    public static get register_bg_image(): string {
        return LzSApp._register_bg_image;
    }
    public static get forgot_pwd_bg_image(): string {
        return LzSApp._forgot_pwd_bg_image;
    }
    public static get powered_by_image(): string {
        return LzSApp._powered_by_image;
    }
    public static get powered_by_url(): string {
        return LzSApp._powered_by_url;
    }
    public static get hide_powered_by(): boolean {
        return LzSApp._hide_powered_by;
    }
    public static get date_format(): string {
        return LzSApp._date_format;
    }
    public static get time_format(): string {
        return LzSApp._time_format;
    }
    public static get datetime_format(): string {
        return LzSApp._datetime_format;
    }
    public static get all_api_calls_extra_params(): LzTOpenObject {
        return LzSApp._all_api_calls_extra_params;
    }

    public static setAllApiCallsExtraParam(key: string, val: any) {
        if (!LzSApp._all_api_calls_extra_params) {
            LzSApp._all_api_calls_extra_params = {};
        }
        LzSApp._all_api_calls_extra_params[key] = val;
    }

    public static getAllApiCallsExtraParam(key: string): any {
        return LzSApp._all_api_calls_extra_params[key];
    }

    private static buildModuleRoutesAndNavs(items: LzTModuleChildClass[]): { routes: LzTRouteEntries; navItems: LzTNavItems } {
        // Initialize the output object
        const out: { routes: LzTRouteEntries; navItems: LzTNavItems } = {
            routes: [],
            navItems: [],
        };

        // Save an array for already built nav keys and route paths
        const routePathsAdded: string[] = [];
        const navKeysAdded: string[] = [];

        const addRoute = (route: LzTRouteEntry) => {
            // If route entry not already added, then
            if (routePathsAdded.indexOf(route.path) < 0) {
                // Cleanup the placeholder vars and change to actual react router vars
                const newPath = route.path.replace('{recordId}', ':id');

                // Create a new entry to avoid overwriting the passed parameter
                const newRoute: LzTRouteEntry = {
                    path: newPath,
                    element: route.element,
                    elementAdd: route.elementAdd,
                    elementEdit: route.elementEdit,
                    elementView: route.elementView,
                };

                // Add it and save to local var for later testing the above check
                out.routes.push(newRoute);
                routePathsAdded.push(newPath);
            }
        };

        // Loop on all items
        for (let item of items) {
            // console.log('AppBS-LoadModuleRoutesNavs-ITEM', item, item.variation, item instanceof LzCModule && lzFIsArray(item.children));

            // Get the children call to pre-load the child data
            let childData = null;
            if (item instanceof LzCModule && lzFIsArray(item.children)) {
                // NOTE: we used instanceOf check above instead of the item.variation check because the later gives error on item.children accessing
                // console.log('AppBS-LoadModuleRoutesNavs-ITEM-loadModuleRoutesAndNavs()', item.children);
                childData = LzSApp.buildModuleRoutesAndNavs(item.children);
                // console.log('AppBS-LoadModuleRoutesNavs-ITEM-childData', childData);
            }

            // If navigation is present in this item
            if (item.navItems) {
                // Loop on all defined navItems and add them
                for (let navItem of item.navItems) {
                    // If not add already
                    if (navItem.key !== '' && navKeysAdded.indexOf(navItem.key) < 0) {
                        // Add the nav item to output and also add the key to avoid being added again
                        out.navItems.push(navItem);
                        navKeysAdded.push(navItem.key);
                    }
                }
            }

            // If nav key does not already exist in the navs, only then try to add it
            if (item.ident !== '' && navKeysAdded.indexOf(item.ident) < 0) {
                // If its a Module
                if ('Module' === item.variation) {
                    // Build the module root navigation
                    const navItem: LzTNavItem = {
                        key: item.ident,
                        rightsPath: item.rightsPath,
                        icon: item.icon,
                        title: item.name,
                    };

                    // If children of that module exist then set them
                    if (childData?.navItems) {
                        navItem.children = childData.navItems;
                    }
                    // Else if no children then add the route of the root entry
                    else {
                        navItem.route = item.fullRoute;
                    }

                    // Add the nav item to output and also add the key to avoid being added again
                    out.navItems.push(navItem);
                    navKeysAdded.push(navItem.key);
                }

                // If its an Entity
                else if ('Entity' === item.variation) {
                    // Add the entity navigation entry point
                    const navItem: LzTNavItem = {
                        key: item.ident,
                        rightsPath: item.rightsPath,
                        icon: item.icon,
                        title: item.name,
                        route: item.fullRoute,
                    };

                    // Add the nav item to output and also add the key to avoid being added again
                    out.navItems.push(navItem);
                    navKeysAdded.push(navItem.key);
                }

                // If its an Report
                else if ('Report' === item.variation) {
                    // Add the report navigation entry point
                    const navItem: LzTNavItem = {
                        key: item.ident,
                        rightsPath: item.rightsPath,
                        icon: item.icon,
                        title: item.name,
                        route: item.fullRoute,
                    };

                    // Add the nav item to output and also add the key to avoid being added again
                    out.navItems.push(navItem);
                    navKeysAdded.push(navItem.key);
                }

                // If its a Page
                else if ('Page' === item.variation) {
                    // Add the page navigation entry
                    const navItem: LzTNavItem = {
                        key: item.ident,
                        rightsPath: item.rightsPath,
                        icon: item.icon,
                        title: item.name,
                        route: item.fullRoute,
                    };

                    // Add the nav item to output and also add the key to avoid being added again
                    out.navItems.push(navItem);
                    navKeysAdded.push(navItem.key);
                }

                // Else not defined
                else {
                    // TODO: Show error somehow
                }
            }

            // If routes are present in this item
            if (item.routes) {
                // Loop on all defined routes and add them
                for (let route of item.routes) {
                    // If not add already
                    if (route.path !== '' && routePathsAdded.indexOf(route.path) < 0) {
                        addRoute(route);
                    }
                }
            }

            // If route path does not already exist in the routes, only then try to add it
            if (item.fullRoute !== '' && routePathsAdded.indexOf(item.fullRoute) < 0) {
                // If its a Module
                if (item instanceof LzCModule) {
                    // Add the module root route for the parent navigation screen
                    const newRoute: LzTRouteEntry = {
                        path: item.fullRoute,
                        element: <LzXPageNavParent key={item.ident + '-page'} private navItems={out.navItems} />,
                    };

                    addRoute(newRoute);
                }

                // Else-If its an Entity
                else if (item instanceof LzCEntity) {
                    // Add the entity routes
                    const newRoute: LzTRouteEntry = {
                        path: item.fullRoute,
                        element: item.listingComponent,
                    };
                    if (item.addComponent) {
                        newRoute.elementAdd = item.addComponent;
                    }
                    if (item.editComponent) {
                        newRoute.elementEdit = item.editComponent;
                    }
                    if (item.viewComponent) {
                        newRoute.elementView = item.viewComponent;
                    }

                    addRoute(newRoute);
                }

                // Else-If its a Report
                else if (item instanceof LzCReport) {
                    // Add the report routes
                    const newRoute: LzTRouteEntry = {
                        path: item.fullRoute,
                        element: item.reportComponent,
                    };

                    addRoute(newRoute);
                }

                // Else-If its a Page and has a children defined (no children means it was a simple link to some inside route or outside url)
                else if (item instanceof LzCPage && typeof item.children !== 'undefined') {
                    // Add the page routes
                    const newRoute: LzTRouteEntry = {
                        path: item.fullRoute,
                        element: item.children,
                    };

                    addRoute(newRoute);
                }

                // Else unknown type
                else {
                    // TODO: Show error somehow
                }
            }

            // Add the child routes
            if (childData?.routes) {
                for (const route of childData.routes) {
                    if (routePathsAdded.indexOf(route.path) < 0) {
                        addRoute(route);
                    }
                }
            }
        }

        return out;
    }

    public static buildRoutesAndNavs(props: LzPAppBootstrap): [LzTRouteEntries, LzTNavItems] {
        // Initialize the routes and navs
        const routes: LzTRouteEntries = props.routes || [];
        const navItems: LzTNavItems = props.navItems || [];

        // If authenticated mode is enabled
        if (props.is_auth_based) {
            // Load flags telling if registration and forgot password are to be added
            const noRegistration = isNull(props.auth_registeration_page);
            const noForgotPwd = isNull(props.auth_forgot_pwd_page);

            // If a special dashboard page was passed (instead user could create a module also)
            if (props.auth_dashboard_page) {
                routes.push({ path: '/', element: props.auth_dashboard_page });
                navItems.push({ key: 'dashboard', rightsPath: '', icon: <DashboardIcon />, title: 'Dashboard', route: '/' });
            }

            // Add the login page route
            routes.push({
                path: '/login',
                element: props.auth_login_page || <LzXPageLogin hideCreateAccountLink={noRegistration} hideForgotPasswordLink={noForgotPwd} />,
            });

            // Add forgot password routes if not denied
            if (!noForgotPwd) {
                routes.push({ path: '/forgot-password', element: props.auth_forgot_pwd_page || <LzXPageForgotPassword /> });
                routes.push({ path: '/reset-password', element: props.auth_reset_pwd_page || <LzXPageResetPassword /> });
            }

            // Add registration page route if not denied
            if (!noRegistration) {
                routes.push({ path: '/register', element: props.auth_registeration_page || <LzXPageRegister /> });
            }
        }

        const modulesRoutesAndNavs = LzSApp.buildModuleRoutesAndNavs(LzSApp.modules);

        return [routes.concat(modulesRoutesAndNavs.routes), navItems.concat(modulesRoutesAndNavs.navItems)];
    }
}
